<script lang="ts">
import { Vue, Options } from 'vue-class-component';
import { Form } from '@/helpers/Form';
import { Prop } from '@/helpers/Decorators';
import FieldConfigsService from '@/modules/low-code/services/FieldConfigsService';
import ScriptsConfigService, {
    SchemaSection,
    SchemaControl,
    FormSection,
    FormModule,
    ModuleField,
    FormControl
} from '@/modules/low-code/services/ScriptsConfigService';

@Options({
    components: {},
    emits: ['scriptConfigSaved', 'closeConfig', 'configIsEmpty']
})
export default class UserConfig extends Vue
{
    @Prop({ default: '' })
    public scriptId: string;

    @Prop({ default: '' })
    public scriptConfigId: string;

    @Prop({ default: false })
    public isModalMode: boolean;

    @Prop({ default: null })
    public moduleId: string;

    public form = Form.create<{
        scriptId: string;
        sections: FormSection[];
    }>({
        scriptId: '',
        sections: []
    });

    public defaultScriptConfigId: string = null;
    public configIsEmpty: boolean = false;
    public modules: FormModule[] = [];
    public sections: SchemaSection[] = [];
    public allModuleFields: Record<string, any[]> = {};
    public moduleFields: Record<string, ModuleField[]> = {};

    public get scriptConfigIdCalc(): string
    {
        if (this.isDefaultMode)
            return this.defaultScriptConfigId;

        return this.scriptConfigId;
    }

    public get isEditMode(): boolean
    {
        return this.scriptConfigIdCalc !== null && this.scriptConfigIdCalc !== '';
    }

    public get isDefaultMode(): boolean
    {
        return this.isModalMode === false;
    }

    public async created(): Promise<void>
    {
        await this.form.ready([
            this.loadModules().then(() => true),
            this.loadSchema().then(() => true)
        ]);

        if (this.isDefaultMode)
            await this.setDefaultScriptConfigId();

        await this.loadData();

        // `loadSchema()` ustawia wartości startowe (w tym defaulty dla nowych pól, które nie istnieją w zapisanej konfiguracji).
        // Tę synchronizację (dociągnięcie pól modułu + ustawienie defaultów w kontrolkach zależnych) uruchamiamy tylko,
        // gdy nie wczytujemy zapisanej konfiguracji, żeby nie robić zbędnych pobrań i nie mieszać w wartościach ustawionych z `loadData()`.
        if (!this.isEditMode)
            await this.applyModuleDefaults();

        this.$emit('configIsEmpty', this.configIsEmpty);

        if (this.isModalMode)
            this.$events.$on('saveConfigForm', (value: boolean) => this.saveForm());
    }

    public async loadModules(): Promise<void>
    {
        try
        {
            const result = await ScriptsConfigService.getModules(this.moduleId);

            this.modules = result.items.map((item: any) => ({
                key: item.result.key,
                value: item.result.value,
                publicId: item.result.publicId
            }));
        }
        catch (ex)
        {
            this.handleError(ex);
        }
    }

    public async getModuleFields(moduleId: string): Promise<void>
    {
        try
        {
            const response = await FieldConfigsService.getFieldConfigDefaultSettings(
                moduleId,
                'Indexing',
                'all'
            );

            this.allModuleFields[moduleId] = Object.entries(response).map(([key, value]: [string, any]) => ({
                key,
                fieldType: value.blueprint?.type ?? value.type,
                value: value.headerName + (value.formName ? ` (${value.formName})` : ''),
                blueprint: value.blueprint
            }));

            this.moduleFields[moduleId] = this.allModuleFields[moduleId].map(({ key, fieldType, value }) => ({
                key,
                fieldType,
                value
            }));
        }
        catch (ex)
        {
            this.handleError(ex);
        }
    }

    public async loadSchema(): Promise<void>
    {
        try
        {
            const response = await ScriptsConfigService.getSchema(this.scriptId, this.moduleId);

            this.sections = response;

            this.form.sections = [];

            this.form.sections = this.sections.map(section => ({
                sectionKey: section.key,
                controls: this.flattenControls(section.controls).reduce((acc, control) =>
                {
                    acc[control.key] = {
                        ...control,
                        value: this.resolveInitialControlValue(control)
                    };

                    return acc;
                }, {} as Record<string, FormControl>)
            }));

            if (this.form.sections.length === 0)
                this.configIsEmpty = true;

        }
        catch (ex)
        {
            this.handleError(ex);
        }
    }

    public flattenControls(controls: any[]): any[]
    {
        const result: any[] = [];

        for (const control of controls)
        {
            result.push(control);

            if (control.relatedControls && control.relatedControls.length > 0)
            {
                result.push(...this.flattenControls(control.relatedControls));
            }
        }

        return result;
    }

    private resolveInitialControlValue(control: SchemaControl): any
    {
        if (control.defaultValue == null)
            return null;

        if (control.type !== 'module')
            return control.defaultValue;

        return this.resolveModuleOption(control.defaultValue);
    }

    private resolveModuleOption(value: any): FormModule | null
    {
        if (value == null)
            return null;

        if (typeof value === 'object')
        {
            if (value.publicId)
                return this.modules.find(p => p.publicId === value.publicId) ?? null;

            if (value.key)
                return this.modules.find(p => p.key === value.key) ?? null;
        }

        const valueAsString = value.toString();

        return this.modules.find(p => p.publicId === valueAsString || p.key === valueAsString) ?? null;
    }

    private async applyModuleDefaults(): Promise<void>
    {
        const moduleIdsToLoad = new Set<string>();
        const controlsToReload: SchemaControl[] = [];

        this.sections.forEach((schemaSection, sectionIndex) =>
        {
            schemaSection.controls.forEach(schemaControl =>
            {
                if (schemaControl.type !== 'module')
                    return;

                const control = this.form.sections[sectionIndex]?.controls[schemaControl.key] as FormControl & { defaultValue?: any };

                if (!control)
                    return;

                const resolvedModule = control.value ?? this.resolveModuleOption(control.defaultValue);

                if (!resolvedModule)
                    return;

                control.value = resolvedModule;

                if (resolvedModule.publicId)
                {
                    moduleIdsToLoad.add(resolvedModule.publicId);
                    controlsToReload.push(schemaControl);
                }
            });
        });

        await Promise.all(
            [...moduleIdsToLoad]
                .filter(publicId => !this.moduleFields[publicId])
                .map(publicId => this.getModuleFields(publicId))
        );

        this.applyRelatedControlDefaults();
        controlsToReload.forEach(control => this.reloadRelatedFields(control));
    }

    private applyRelatedControlDefaults(): void
    {
        this.sections.forEach((schemaSection, sectionIndex) =>
        {
            schemaSection.controls.forEach(schemaControl =>
            {
                if (schemaControl.type !== 'module')
                    return;

                const moduleControl = this.form.sections[sectionIndex]?.controls[schemaControl.key] as FormControl;
                const modulePublicId = moduleControl?.value?.publicId;

                if (!modulePublicId || !this.moduleFields[modulePublicId])
                    return;

                const moduleFieldOptions = this.moduleFields[modulePublicId];

                schemaControl.relatedControls?.forEach(relatedControl =>
                {
                    const formRelatedControl = this.form.sections[sectionIndex]?.controls[relatedControl.key] as FormControl & { defaultValue?: any };

                    if (!formRelatedControl)
                        return;

                    const resolvedModuleField = this.resolveModuleFieldOption(
                        formRelatedControl.value ?? formRelatedControl.defaultValue,
                        moduleFieldOptions
                    );

                    if (resolvedModuleField)
                        formRelatedControl.value = resolvedModuleField;

                    if (!relatedControl.relatedControls?.length)
                        return;

                    const tableFieldOptions = this.getTableFieldsPerControl(sectionIndex, relatedControl);

                    relatedControl.relatedControls.forEach(tableControl =>
                    {
                        const formTableControl = this.form.sections[sectionIndex]?.controls[tableControl.key] as FormControl & { defaultValue?: any };

                        if (!formTableControl)
                            return;

                        const resolvedTableField = this.resolveModuleFieldOption(
                            formTableControl.value ?? formTableControl.defaultValue,
                            tableFieldOptions
                        );

                        if (resolvedTableField)
                            formTableControl.value = resolvedTableField;
                    });
                });
            });
        });
    }

    private resolveModuleFieldOption(value: any, options: ModuleField[]): ModuleField | null
    {
        if (value == null || !Array.isArray(options) || options.length === 0)
            return null;

        if (typeof value === 'object')
        {
            if (value.key)
                return options.find(p => p.key === value.key) ?? null;

            if (value.value)
            {
                const valueLabel = value.value.toString().toLowerCase();

                return options.find(p => p.value?.toString().toLowerCase() === valueLabel) ?? null;
            }
        }

        const valueAsString = value.toString();
        const valueAsStringLower = valueAsString.toLowerCase();

        return options.find(p =>
            p.key === valueAsString ||
            p.key?.toString().toLowerCase() === valueAsStringLower ||
            p.value?.toString().toLowerCase() === valueAsStringLower
        ) ?? null;
    }

    public async loadData(): Promise<void>
    {
        try
        {
            let configId = this.scriptConfigIdCalc;

            if (!this.isEditMode)
            {
                if (this.isDefaultMode)
                    return;

                const defaultScriptId = await this.getDefaultScriptConfigId();

                if (defaultScriptId === null || defaultScriptId === '')
                    return;

                configId = defaultScriptId;
            }

            const result = (await ScriptsConfigService.getConfig(configId, this.moduleId)).result;

            for (const section of result.sections)
            {
                const formSection = this.form.sections.find(p => p.sectionKey === section.sectionKey);

                if (!formSection) continue;

                for (const [key, value] of Object.entries<any>(section.controls))
                {
                    const formControl = formSection.controls[key];

                    if (formControl)
                    {
                        if (formControl.type === 'module')
                        {
                            const schemaSection = this.sections.find(p => p.key === section.sectionKey);
                            const schemaControl = schemaSection
                                ? this.flattenControls(schemaSection.controls).find(p => p.key === key)
                                : null;
                            const module = this.resolveModuleOption(value?.value?.publicId ?? value?.value?.key ?? value?.value);

                            if (module && !this.moduleFields[module.publicId])
                                await this.getModuleFields(module.publicId);

                            formControl.value = module ?? null;

                            if (schemaControl)
                                this.reloadRelatedFields(schemaControl);
                        }
                        else
                        {
                            formControl.value = value?.value;
                        }
                    }
                }
            }

            this.applyRelatedControlDefaults();
        }
        catch (ex)
        {
            this.handleError(ex);
        }
    }

    public async getDefaultScriptConfigId(): Promise<string>
    {
        try
        {
            return (await ScriptsConfigService.getDefaultConfigId(this.scriptId, this.moduleId));
        }
        catch (ex)
        {
            this.handleError(ex);
        }
    }

    public async setDefaultScriptConfigId(): Promise<void>
    {
        this.defaultScriptConfigId = await this.getDefaultScriptConfigId();
    }

    public async saveForm(): Promise<void>
    {
        try
        {
            this.form.wait();

            let configId = this.scriptConfigIdCalc;

            const form = this.form.formatData();

            form.scriptId = this.scriptId;

            if (this.isEditMode)
                await ScriptsConfigService.updateConfig(this.scriptConfigIdCalc, form, this.moduleId);
            else
            {
                configId = (await ScriptsConfigService.createConfig(form, this.isDefaultMode, this.moduleId)).result.publicId;
            }

            this.$alert.success(this.$t('[[[Konfiguracja została zapisana.]]]'));

            if (!this.isModalMode)
            {
                this.redirect({ name: 'lowCode-scripts' });
            }
            else
            {
                this.$emit('scriptConfigSaved', configId);
            }
        }
        catch (ex)
        {
            this.handleError(ex);
        }
        finally
        {
            this.form.continue();
        }
    }

    public getModuleFieldsPerControl(sectionIndex: number, control: SchemaControl): ModuleField[]
    {
        const formSection = this.form.sections[sectionIndex];
        const relatedModule = this.findParentControl(this.sections[sectionIndex], control.key);

        if (relatedModule === null)
            return [];

        const module = formSection.controls[relatedModule.key];

        if (!module || module.value === null)
            return [];

        if (!this.moduleFields[module.value.publicId])
            return [];

        return this.moduleFields[module.value.publicId].filter(p => p.fieldType === control.type || (control.type === 'date' && p.fieldType === 'DateTime'));
    }

    public getTableFieldsPerControl(sectionIndex: number, control: SchemaControl): ModuleField[]
    {
        const formSection = this.form.sections[sectionIndex];
        const relatedModule = this.findParentControl(this.sections[sectionIndex], control.key);

        if (relatedModule === null)
            return [];

        const module = formSection.controls[relatedModule.key];

        if (!module || module.value === null)
            return [];

        if (!this.moduleFields[module.value.publicId])
            return [];

        const formTableField = formSection.controls[control.key];

        if (!formTableField?.value) return [];

        const relatedTableValue = this.allModuleFields[module.value.publicId].find(p => p.fieldType === 'table' && p.key === formTableField.value.key);

        if (!relatedTableValue?.blueprint?.columns) return [];

        return relatedTableValue.blueprint.columns.map((column: any, index: number) => ({
            key: relatedTableValue.blueprint.components[index].name,
            fieldType: column.columnType.type,
            value: this.$i18n.currentTranslation(column.columnHeader)
        }));
    }

    public findParentControl(section: SchemaSection, targetKey: string): SchemaControl
    {
        for (const control of section.controls)
        {
            if (control.relatedControls?.some(related => related.key === targetKey))
                return control;
        }

        return null;
    }

    public unmounted(): void
    {
        if (this.isModalMode)
        {
            this.$events.$off("saveConfigForm");
            this.$emit('closeConfig');
        }
    }

    public async handleModuleChange(selectedModule: FormModule, schemaSection: SchemaSection, schemaControl: SchemaControl): Promise<void>
    {
        if (!selectedModule)
        {
            this.clearRelatedFields(schemaSection, schemaControl);
            this.reloadRelatedFields(schemaControl);
            schemaControl.relatedControls.forEach(tableControl =>
            {
                this.clearRelatedFields(schemaSection, tableControl);
                this.reloadRelatedFields(tableControl);
            });

            return;
        }

        if (!this.moduleFields[selectedModule.publicId])
            await this.getModuleFields(selectedModule.publicId);

        this.clearRelatedFields(schemaSection, schemaControl);
        this.reloadRelatedFields(schemaControl);
        schemaControl.relatedControls.forEach(tableControl =>
        {
            this.clearRelatedFields(schemaSection, tableControl);
            this.reloadRelatedFields(tableControl);
        });
    }

    public async handleTableChange(selectedTable: any, schemaSection: SchemaSection, schemaControl: SchemaControl): Promise<void>
    {
        this.clearRelatedFields(schemaSection, schemaControl);
        await this.reloadRelatedFields(schemaControl);
    }

    public reloadRelatedFields(schemaControl: SchemaControl): void
    {
        schemaControl.relatedControls.forEach(control =>
        {
            control.reloadKey = (control.reloadKey ?? 0) + 1;
        });
    }

    public clearRelatedFields(schemaSection: SchemaSection, schemaControl: SchemaControl): void
    {
        const formSection = this.form.sections.find(p => p.sectionKey === schemaSection.key);

        schemaControl.relatedControls.forEach(control =>
        {
            formSection.controls[control.key].value = null;
        });
    }

    private handleError(ex: any): void
    {
        this.handleException(ex, {
            400: (ex: any) => this.$alert.warning(ex.message),
            422: (ex: any) => [
                this.form.$errors.record(ex.data.errors),
                this.$nextTick(() => this.$scrollTo('.invalid-field')),
                this.$nextTick(() =>
                    this.$alert.warning(this.$t('[[[Nie wszystkie pola zostały wypełnione prawidłowo.]]]'))
                ),
            ],
        });
    }
}
</script>

<template>
    <DataCard :use-panels="true" :overflow="true">
        <template v-if="!isModalMode" #toolbar>
            <ActionBar :toggle-footer="true">
                <BackButton toolbar :fallback="{ name:'lowCode-scripts' }" />
                <ActionButton
                    v-if="!configIsEmpty"
                    variant="success"
                    form="script-user-config"
                    icon="fas fa-check-circle"
                    :text="$t('[[[Zapisz]]]')"
                    :disabled="!form.active()"
                    @click="saveForm"
                />
            </ActionBar>
        </template>
        <template #default>
            <div class="row">
                <div class="col">
                    <div v-if="configIsEmpty" class="alert alert-primary" role="alert">
                        {{ $t('[[[Konfiguracja nie została ustawiona.]]]') }}
                    </div>
                    <form
                        id="script-user-config"
                        @keydown="form.$errors.clear($event.target.name)"
                    >
                        <fieldset>
                            <PlaceholderForm :loading="form.loading()">
                                <IdeoAccordion v-for="(section, sectionIndex) in sections" :key="section.key">
                                    <IdeoAccordionItem :initial-open="sections && sections.length === 1">
                                        <template #header>
                                            <div class="accordion-heading d-flex justify-content-between align-items-center">
                                                <div class="text-primary text-decoration-none me-3 p-0">
                                                    <strong>{{ $t(section.name) }}</strong>
                                                </div>
                                            </div>
                                        </template>
                                        <div>
                                            <div class="card-body">
                                                <div>
                                                    <template v-for="control in section.controls" :key="control.key">
                                                        <IdeoFormGroup
                                                            :label="$t(control.name)"
                                                            :required="control.required"
                                                            :invalid-feedback="form.$errors.first(`sections[${sectionIndex}].controls.${control.key}`)"
                                                            :state="form.$errors.state(`sections[${sectionIndex}].controls.${control.key}`)"
                                                        >
                                                            <IdeoFormInput
                                                                v-if="control.type == 'text'"
                                                                v-model="form.sections[sectionIndex].controls[control.key].value"
                                                                type="text"
                                                            />
                                                            <IdeoDatetime
                                                                v-if="control.type == 'date'"
                                                                v-model="form.sections[sectionIndex].controls[control.key].value"
                                                                type="datetime"
                                                            />
                                                            <IdeoFormCheckbox
                                                                v-if="control.type == 'boolean'"
                                                                v-model="form.sections[sectionIndex].controls[control.key].value"
                                                            />
                                                            <IdeoFormInput
                                                                v-if="control.type == 'numeric'"
                                                                v-model="form.sections[sectionIndex].controls[control.key].value"
                                                                type="number"
                                                            />
                                                            <IdeoSelect
                                                                v-if="control.type == 'module'"
                                                                v-model="form.sections[sectionIndex].controls[control.key].value"
                                                                :options="modules"
                                                                @update:modelValue="handleModuleChange($event, section, control)"
                                                            />
                                                        </IdeoFormGroup>

                                                        <div v-if="control.relatedControls" class="mb-4">
                                                            <template v-for="moduleControl in control.relatedControls" :key="moduleControl.key">
                                                                <IdeoFormGroup
                                                                    :label="$t(moduleControl.name)"
                                                                    :required="moduleControl.required"
                                                                    :invalid-feedback="form.$errors.first(`sections[${sectionIndex}].controls.${moduleControl.key}`)"
                                                                    :state="form.$errors.state(`sections[${sectionIndex}].controls.${moduleControl.key}`)"
                                                                >
                                                                    <IdeoSelect
                                                                        :key="moduleControl.reloadKey"
                                                                        v-model="form.sections[sectionIndex].controls[moduleControl.key].value"
                                                                        :placeholder="$t('[[[Wybierz...]]]')"
                                                                        :options="getModuleFieldsPerControl(sectionIndex, moduleControl)"
                                                                        @update:modelValue="handleTableChange($event, section, moduleControl)"
                                                                    />
                                                                </IdeoFormGroup>

                                                                <div v-if="moduleControl.relatedControls" class="mb-4">
                                                                    <template v-for="tableControl in moduleControl.relatedControls" :key="tableControl.key">
                                                                        <IdeoFormGroup
                                                                            :label="$t(tableControl.name)"
                                                                            :required="tableControl.required"
                                                                            :invalid-feedback="form.$errors.first(`sections[${sectionIndex}].controls.${tableControl.key}`)"
                                                                            :state="form.$errors.state(`sections[${sectionIndex}].controls.${tableControl.key}`)"
                                                                        >
                                                                            <IdeoSelect
                                                                                :key="tableControl.reloadKey"
                                                                                v-model="form.sections[sectionIndex].controls[tableControl.key].value"
                                                                                :placeholder="$t('[[[Wybierz...]]]')"
                                                                                :options="getTableFieldsPerControl(sectionIndex, moduleControl)"
                                                                            />
                                                                        </IdeoFormGroup>
                                                                    </template>
                                                                </div>
                                                            </template>
                                                        </div>
                                                    </template>
                                                </div>
                                            </div>
                                        </div>
                                    </IdeoAccordionItem>
                                </IdeoAccordion>
                            </PlaceholderForm>
                        </fieldset>
                    </form>
                </div>
            </div>
        </template>
    </DataCard>
</template>
